import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { PublicFooter, PublicHeader } from "@/components/PublicMarketingLayout";

const benefits = [
  "Start with one free blueprint",
  "No coding required",
  "Ready for Replit, Lovable, Bolt, Cursor and Claude Code",
];

export function AuthMarketingLayout({
  title,
  children,
  legalNotice,
  accountSwitch,
}: {
  title: string;
  children: ReactNode;
  legalNotice?: ReactNode;
  accountSwitch: ReactNode;
}) {
  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-white via-indigo-50/70 to-violet-50 text-slate-950">
      <PublicHeader />

      <div className="mx-auto grid w-full max-w-[1200px] items-center gap-8 px-4 py-6 sm:px-6 sm:py-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-12 lg:py-10">
        <section className="hidden max-w-xl lg:block">
          <p className="w-fit rounded-full border border-indigo-200 bg-white px-4 py-1.5 text-sm font-semibold text-indigo-700 shadow-sm">
            Plan before you build
          </p>
          <h1 className="mt-5 text-5xl font-bold tracking-tight text-slate-950 xl:text-6xl">
            Turn your idea into a build-ready plan
          </h1>
          <p className="mt-5 max-w-lg text-base leading-8 text-slate-600">
            Create clear product specifications, data models, acceptance
            criteria, edge cases, test scenarios and implementation prompts
            before your AI builder writes the first line of code.
          </p>
          <ul className="mt-7 grid gap-3">
            {benefits.map((benefit) => (
              <li
                key={benefit}
                className="flex items-center gap-3 rounded-2xl border border-indigo-100 bg-white/85 p-3.5 text-sm font-bold text-slate-800 shadow-sm"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-700">
                  ✓
                </span>
                {benefit}
              </li>
            ))}
          </ul>

          <div className="mt-7 rounded-[2rem] border border-indigo-100 bg-white/85 p-5 shadow-xl shadow-indigo-100/60">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-indigo-500">
                Blueprint preview
              </p>
              <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">
                Ready
              </span>
            </div>
            <div className="grid gap-3 rounded-[1.5rem] bg-gradient-to-br from-indigo-50 to-white p-4">
              <div className="h-3 w-28 rounded-full bg-indigo-200" />
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="rounded-xl bg-white p-3 text-xs font-bold text-slate-700 shadow-sm">
                  Product spec
                </div>
                <div className="rounded-xl bg-white p-3 text-xs font-bold text-slate-700 shadow-sm">
                  Data model
                </div>
                <div className="rounded-xl bg-white p-3 text-xs font-bold text-slate-700 shadow-sm">
                  Test scenarios
                </div>
                <div className="rounded-xl bg-indigo-600 p-3 text-xs font-bold text-white shadow-sm">
                  Build prompt
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="flex items-center justify-center lg:min-h-[620px]">
          <div className="w-full max-w-[440px]">
            <div className="mb-5 text-center lg:hidden">
              <h1 className="text-3xl font-bold tracking-tight text-slate-950">
                {title}
              </h1>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                Turn your idea into a build-ready plan.
              </p>
            </div>

            <div
              className="mx-auto flex w-full max-w-[440px] flex-col items-center justify-center rounded-2xl border border-indigo-100 bg-white/95 p-5 shadow-2xl shadow-indigo-100/70 sm:p-7 [&_.cl-card]:w-full [&_.cl-cardBox]:w-full [&_.cl-rootBox]:w-full"
              data-testid="auth-form-wrapper"
            >
              {children}
              {legalNotice ? (
                <p className="mt-4 max-w-sm text-center text-xs leading-5 text-slate-500">
                  {legalNotice}
                </p>
              ) : null}
              <div className="mt-4 text-center text-sm leading-6 text-slate-600">
                {accountSwitch}
              </div>
              <p className="mt-2 text-center text-sm">
                <Link to="/" className="font-semibold text-indigo-700 hover:text-indigo-900">
                  Back to homepage
                </Link>
              </p>
            </div>
          </div>
        </section>
      </div>
      <PublicFooter />
    </div>
  );
}
